import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  recordingConsentStepVariant,
  sessionRecordingExplainerVersion,
  type SetupRow,
  type SetupStepId,
} from '@shared/setup'
import { RECORDING_POLICY_EASY_READ_COPY } from '@shared/calls/recording-consent'
import { useAuth } from '../../../contexts/auth-context'
import { updateMySetupState } from '../../../services/appsync-client'
import { canOpenAppSettings } from '../../../services/permissions/permissions-service'
import {
  acceptRecordingPolicy,
  declineRecordingPolicy,
} from '../../../services/recording-policy-service'
import { patchSetupProfile } from '../../../stores/setup-profile-store'
import { ONBOARDING_TOUR_ATTR } from '../../onboarding-tour/tour-start-gate'
import { runStepAction } from '../data/setup-actions'
import {
  DONE_COPY,
  OVERVIEW_COPY,
  PHONE_SILENT_NOTICE,
  PHONE_WONT_ASK_NOTICE,
  REMINDER_COPY,
  reminderLine,
  STEP_LIST_TITLES,
  STILL_OFF_NOTICE,
  stepCopy,
  thingsStillToDo,
  TRY_AGAIN_NOTICE,
  TURNED_ON,
  type CopyContext,
} from '../data/setup-copy'
import { markOptionalAsked, noteDismissals } from '../data/setup-state'
import { outstandingRequired, overviewVariant, type SetupMode } from '../utils/setup-model'
import { RecordingConsentContent } from './recording-consent-content'
import { SetupStepCard } from './setup-step-card'

export const SETUP_FLOW_ATTR = 'data-setup-flow'

/** Steps whose "Allow" button asks the phone (or browser) a yes/no question. */
const DEVICE_PERMISSION_STEPS: ReadonlySet<SetupStepId> = new Set(['notifications', 'microphone', 'camera', 'location'])

export type SetupFlowOutcome = 'completed' | 'dismissed'

export type SetupFlowProps = {
  mode: SetupMode
  /** The steps to walk, fixed when the flow opened. */
  walk: SetupStepId[]
  /** Live rows (re-probed on foreground and after each action). */
  rows: SetupRow[]
  copy: CopyContext
  /** Open straight on this step (push tap / deep link). */
  startStep?: SetupStepId
  /** Open on the first step of the walk, skipping the reminder card (banner tap). */
  startOnFirstStep?: boolean
  resetRun: boolean
  consentRequestPending: boolean
  devicePending: boolean
  /** iPhone / iPad Safari off the Home Screen: no web alerts. */
  showUseAppTip: boolean
  tourNext: boolean
  roleType: string | null
  /** The account's current call-recording answer (shown when revisiting the step). */
  consentStatus: 'ACCEPTED' | 'DECLINED' | null
  /** A gate (call, recording, travel…) is holding setup off screen; state is kept. */
  hidden: boolean
  /** Re-run the device checks; resolves once rows are fresh. */
  onRefresh: () => Promise<void>
  /** Something changed that the server should hear about (setup report). */
  onChanged: () => void
  onClose: (outcome: SetupFlowOutcome) => void
}

type Screen = { kind: 'overview' } | { kind: 'reminder' } | { kind: 'step'; index: number } | { kind: 'done' }

function initialScreen(mode: SetupMode, startStep: SetupStepId | undefined, startOnFirstStep: boolean): Screen {
  if (startStep || startOnFirstStep) return { kind: 'step', index: 0 }
  if (mode === 'reminder') return { kind: 'reminder' }
  return { kind: 'overview' }
}

function CheckBadge({ done }: { done: boolean }) {
  return done ? (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700" aria-hidden>
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4">
        <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : (
    <span className="h-6 w-6 shrink-0 rounded-full border-2 border-slate-300" aria-hidden />
  )
}

const primaryClass =
  'min-h-12 w-full rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-3 text-base font-semibold text-white shadow-md shadow-brand-600/25 outline-none transition hover:from-brand-600 hover:to-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2'
const laterClass =
  'min-h-11 w-full rounded-xl px-4 py-2.5 text-base font-medium text-slate-500 outline-none transition hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-brand-500'

/**
 * The one setup modal. Centered, identical on the desktop rail and the mobile
 * bottom-nav layouts. Never blocks the app: every screen can be put off.
 */
export function SetupFlow({
  mode,
  walk,
  rows,
  copy,
  startStep,
  startOnFirstStep = false,
  resetRun,
  consentRequestPending,
  devicePending,
  showUseAppTip,
  tourNext,
  roleType,
  consentStatus,
  hidden,
  onRefresh,
  onChanged,
  onClose,
}: SetupFlowProps) {
  const { user } = useAuth()
  const userId = user?.userId ?? null
  const [screen, setScreen] = useState<Screen>(() => initialScreen(mode, startStep, startOnFirstStep))
  const [busy, setBusy] = useState(false)
  const [consentBusy, setConsentBusy] = useState<'accept' | 'decline' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [announce, setAnnounce] = useState('')
  const [consentResult, setConsentResult] = useState<string | null>(null)
  const [attempts, setAttempts] = useState<Partial<Record<SetupStepId, number>>>({})
  // An "Allow" tap the phone answered with a hard no (or twice with silence): show the Settings
  // card even if the device check still reads "not asked yet", so the button is never dead.
  const [forcedBlocked, setForcedBlocked] = useState<ReadonlySet<SetupStepId>>(() => new Set())
  const [notice, setNotice] = useState<{ id: SetupStepId; text: string } | null>(null)
  const unanswered = useRef<Partial<Record<SetupStepId, number>>>({})
  /** After the next fresh checks: if this step is still not done, say `text`. */
  const sayIfStillOff = useRef<{ id: SetupStepId; text: string } | null>(null)
  const [checkedTick, setCheckedTick] = useState(0)
  const [steps, setSteps] = useState<SetupStepId[]>(() => (startStep ? [startStep] : walk))
  const dialogRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const headingId = useId()
  const closedRef = useRef(false)

  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])
  const currentId = screen.kind === 'step' ? steps[screen.index] : null
  const liveRow = currentId ? rowById.get(currentId) : undefined
  const currentRow =
    liveRow && currentId && forcedBlocked.has(currentId) && liveRow.state !== 'done'
      ? { ...liveRow, state: 'blocked' as const }
      : liveRow
  const liveRowRef = useRef(liveRow)
  liveRowRef.current = liveRow

  const close = useCallback(
    (outcome: SetupFlowOutcome) => {
      if (closedRef.current) return
      closedRef.current = true
      onClose(outcome)
    },
    [onClose],
  )

  const stepsRef = useRef(steps)
  stepsRef.current = steps

  /** Move on from step `index` (no-op if the person already moved elsewhere). */
  const advanceFrom = useCallback((index: number) => {
    setError(null)
    setNotice(null)
    setScreen((prev) => {
      if (prev.kind !== 'step' || prev.index !== index) return prev
      const next = index + 1
      return next < stepsRef.current.length ? { kind: 'step', index: next } : { kind: 'done' }
    })
  }, [])

  const advance = useCallback(() => {
    setError(null)
    setNotice(null)
    setScreen((prev) => {
      if (prev.kind !== 'step') return prev
      const next = prev.index + 1
      return next < stepsRef.current.length ? { kind: 'step', index: next } : { kind: 'done' }
    })
  }, [])

  // Focus the heading (never a button) whenever the screen changes or setup comes back on screen.
  useEffect(() => {
    if (hidden) return
    const id = window.setTimeout(() => headingRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [screen, hidden])

  const timers = useRef<number[]>([])
  useEffect(
    () => () => {
      for (const id of timers.current) window.clearTimeout(id)
    },
    [],
  )
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms))
  }, [])

  // A step granted in Settings (or by another device) ticks and moves on by itself.
  const announcedDone = useRef<string | null>(null)
  useEffect(() => {
    if (screen.kind !== 'step' || !currentId || !liveRow) return
    if (liveRow.state !== 'done' || liveRow.level === 'info' || currentId === 'recording-consent') {
      announcedDone.current = null
      return
    }
    if (announcedDone.current === currentId) return
    // A done step met on a manual revisit stays until "Next".
    if (mode === 'manual' && (attempts[currentId] ?? 0) === 0) return
    announcedDone.current = currentId
    setForcedBlocked((prev) => {
      if (!prev.has(currentId)) return prev
      const next = new Set(prev)
      next.delete(currentId)
      return next
    })
    setAnnounce(TURNED_ON)
    onChanged()
    const index = screen.index
    later(() => advanceFrom(index), 700)
  }, [screen, currentId, liveRow, mode, attempts, advanceFrom, later, onChanged])

  // Nothing left to show on this screen (a step stopped applying): finish.
  useEffect(() => {
    if (screen.kind === 'step' && screen.index >= steps.length) setScreen({ kind: 'done' })
  }, [screen, steps.length])

  const startWalk = useCallback(
    (index = 0) => {
      setError(null)
      setScreen(steps.length > 0 ? { kind: 'step', index } : { kind: 'done' })
    },
    [steps.length],
  )

  const putOff = useCallback(
    (id: SetupStepId) => {
      const row = rowById.get(id)
      if (userId && row) {
        if (row.level === 'optional') markOptionalAsked(userId, id)
        else if (row.level === 'required') noteDismissals(userId, [id])
      }
      onChanged()
      advance()
    },
    [rowById, userId, onChanged, advance],
  )

  const runPrimary = useCallback(async () => {
    if (!currentId || !currentRow || busy) return
    setBusy(true)
    setError(null)
    try {
      if (currentId === 'session-recording-info') {
        const version = sessionRecordingExplainerVersion({
          role: { roleType },
          consentGiver: copy.consentGiver,
          premiumSessionRecording: copy.premiumSessionRecording,
        })
        if (version && userId) {
          try {
            const res = await updateMySetupState({ explainersSeen: { sessionRecording: version } })
            patchSetupProfile(userId, { setupState: res.setupState })
          } catch {
            /* shown again next time; never holds the person up */
          }
        }
        onChanged()
        advance()
        return
      }
      const result = await runStepAction(currentId, {
        blocked: currentRow.state === 'blocked',
        identity: user?.email || userId || 'ascend-user',
        biometricLabel: copy.biometricLabel,
        attempt: attempts[currentId] ?? 0,
      })
      setAttempts((prev) => ({ ...prev, [currentId]: (prev[currentId] ?? 0) + 1 }))
      if (result !== 'no-answer') unanswered.current[currentId] = 0
      if (result === 'no-settings') {
        setError('Couldn’t open Settings from here. Open your phone’s Settings app and choose Ascend Messenger.')
      } else if (result === 'denied' && canOpenAppSettings()) {
        // The phone won't show its question again: only Settings can turn this on.
        setForcedBlocked((prev) => new Set(prev).add(currentId))
        setNotice({ id: currentId, text: PHONE_WONT_ASK_NOTICE })
      } else if (result === 'denied' || result === 'timed-out') {
        // A browser can still ask (or the phone is still showing its question): no Settings card.
        setNotice({ id: currentId, text: TRY_AGAIN_NOTICE })
      } else if (result === 'no-answer') {
        const silent = (unanswered.current[currentId] ?? 0) + 1
        unanswered.current[currentId] = silent
        if (silent >= 2 && canOpenAppSettings()) {
          setForcedBlocked((prev) => new Set(prev).add(currentId))
          setNotice({ id: currentId, text: PHONE_SILENT_NOTICE })
        } else {
          setNotice({ id: currentId, text: TRY_AGAIN_NOTICE })
        }
      } else {
        setNotice(null)
        // The phone answered but the step may still read "off" once the checks are fresh.
        if (result === 'answered' && DEVICE_PERMISSION_STEPS.has(currentId)) {
          sayIfStillOff.current = { id: currentId, text: TRY_AGAIN_NOTICE }
        }
      }
      await onRefresh()
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'That didn’t work. Try again.')
    } finally {
      setCheckedTick((n) => n + 1)
      setBusy(false)
    }
  }, [currentId, currentRow, busy, roleType, copy, userId, user?.email, attempts, onChanged, advance, onRefresh])

  const checkAgain = useCallback(async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    setAttempts((prev) => (currentId ? { ...prev, [currentId]: (prev[currentId] ?? 0) + 1 } : prev))
    if (currentId) sayIfStillOff.current = { id: currentId, text: STILL_OFF_NOTICE }
    try {
      await onRefresh()
    } finally {
      setCheckedTick((n) => n + 1)
      setBusy(false)
    }
  }, [currentId, onRefresh])

  // A tap must answer back: once the fresh checks are in, say so if the step is still off.
  useEffect(() => {
    const pending = sayIfStillOff.current
    if (!pending || checkedTick === 0) return
    sayIfStillOff.current = null
    const row = liveRowRef.current
    if (row && row.id === pending.id && row.state !== 'done') setNotice({ id: pending.id, text: pending.text })
  }, [checkedTick])

  const consentVariant = recordingConsentStepVariant({ roleType }) ?? 'consent'
  const consentContext = consentRequestPending ? 'resend' : 'setup'
  const decideConsent = useCallback(
    async (accept: boolean) => {
      setConsentBusy(accept ? 'accept' : 'decline')
      setError(null)
      try {
        if (accept) await acceptRecordingPolicy(consentContext)
        else await declineRecordingPolicy(consentContext)
        const done = RECORDING_POLICY_EASY_READ_COPY.consent
        // Saved: the answers go away (no second write) and the result shows until we move on.
        setConsentResult(consentVariant === 'acknowledge' ? 'Thank you.' : accept ? done.afterAccept : done.afterDecline)
        onChanged()
        if (screen.kind === 'step') {
          const index = screen.index
          later(() => {
            setConsentResult(null)
            advanceFrom(index)
          }, 1200)
        }
        return true
      } catch {
        setError(
          accept
            ? 'Couldn’t save your answer. Check your connection and try again.'
            : 'Couldn’t save your choice. Check your connection and try again.',
        )
        return false
      } finally {
        setConsentBusy(null)
      }
    },
    [consentContext, consentVariant, onChanged, screen, later, advanceFrom],
  )

  const remindLater = useCallback(() => {
    if (userId) noteDismissals(userId, stepsRef.current)
    close('dismissed')
  }, [userId, close])

  // Esc: the "later" choice of the current screen. Tab stays inside the dialog.
  useEffect(() => {
    if (hidden) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (screen.kind === 'step' && currentId) putOff(currentId)
        else if (screen.kind === 'done') close('completed')
        else if (screen.kind === 'reminder') remindLater()
        else close('dismissed')
        return
      }
      if (e.key !== 'Tab') return
      const root = dialogRef.current
      if (!root) return
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [hidden, screen, currentId, putOff, close, remindLater])

  // The steps list is fixed at open; a step that stopped applying (role change) is skipped.
  useEffect(() => {
    setSteps((prev) => {
      const kept = prev.filter((id) => rowById.has(id))
      return kept.length === prev.length ? prev : kept
    })
  }, [rowById])

  if (hidden) return null

  const open = outstandingRequired(rows)
  const variant = overviewVariant(rows, steps)
  const progress = screen.kind === 'step' && steps.length > 1 ? `${screen.index + 1} of ${steps.length}` : null

  let content: ReactNode
  if (screen.kind === 'overview') {
    const o = variant === 'all-done' ? OVERVIEW_COPY.allDone : variant === 'new' ? OVERVIEW_COPY.new : OVERVIEW_COPY.someDone
    content = (
      <>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight text-slate-900 outline-none">
          {o.title}
        </h2>
        {resetRun && <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-[15px] font-medium text-sky-900">{OVERVIEW_COPY.adminReset}</p>}
        <ul className="mt-2 space-y-1">
          {o.lines.map((line) => (
            <li key={line} className="text-[17px] leading-relaxed text-slate-700">
              {line}
            </li>
          ))}
        </ul>
        <ul className="mt-4 space-y-1.5" aria-label="Set up steps">
          {rows.map((row) => {
            const done = row.state === 'done'
            const label = STEP_LIST_TITLES[row.id]
            const index = steps.indexOf(row.id)
            const inner = (
              <>
                <CheckBadge done={done} />
                <span className={`flex-1 text-left text-[15px] ${done ? 'text-slate-500' : 'text-slate-800'}`}>{label}</span>
                <span className="sr-only">{done ? 'Done' : 'To do'}</span>
              </>
            )
            return (
              <li key={row.id}>
                {mode === 'manual' ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (index >= 0) startWalk(index)
                      else {
                        setSteps((prev) => [row.id, ...prev.filter((s) => s !== row.id)])
                        setScreen({ kind: 'step', index: 0 })
                      }
                    }}
                    className="flex min-h-11 w-full items-center gap-3 rounded-xl px-2.5 py-2 outline-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    {inner}
                  </button>
                ) : (
                  <div className="flex min-h-11 items-center gap-3 rounded-xl px-2.5 py-2">{inner}</div>
                )}
              </li>
            )
          })}
        </ul>
        {devicePending && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[15px] text-amber-900">{OVERVIEW_COPY.devicePending}</p>}
        {showUseAppTip && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-[15px] text-slate-700">{OVERVIEW_COPY.iosSafari}</p>}
        <div className="mt-5 flex flex-col gap-2">
          <button type="button" onClick={() => (variant === 'all-done' ? close('completed') : startWalk(0))} className={primaryClass}>
            {o.primary}
          </button>
          {variant !== 'all-done' && (
            <button type="button" onClick={() => close('dismissed')} className={laterClass}>
              Not now
            </button>
          )}
        </div>
      </>
    )
  } else if (screen.kind === 'reminder') {
    content = (
      <>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight text-slate-900 outline-none">
          {REMINDER_COPY.title}
        </h2>
        <p className="mt-2 text-[17px] leading-relaxed text-slate-700">{reminderLine(Math.max(steps.length, 1))}</p>
        <ul className="mt-3 space-y-1.5" aria-label="Still to do">
          {steps.map((id) => (
            <li key={id} className="flex min-h-11 items-center gap-3 rounded-xl px-2.5 py-2">
              <CheckBadge done={false} />
              <span className="text-[15px] text-slate-800">{STEP_LIST_TITLES[id]}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-col gap-2">
          <button type="button" onClick={() => startWalk(0)} className={primaryClass}>
            {REMINDER_COPY.primary}
          </button>
          <button type="button" onClick={remindLater} className={laterClass}>
            {REMINDER_COPY.later}
          </button>
        </div>
      </>
    )
  } else if (screen.kind === 'done') {
    const allDone = open.length === 0
    content = (
      <>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight text-slate-900 outline-none">
          {allDone ? DONE_COPY.allDone.title : DONE_COPY.almost.title}
        </h2>
        {allDone ? (
          <p className="mt-2 text-[17px] leading-relaxed text-slate-700">{tourNext ? DONE_COPY.tourNext : DONE_COPY.tourLater}</p>
        ) : (
          <>
            <p className="mt-2 text-[17px] leading-relaxed text-slate-700">{thingsStillToDo(open.length)}</p>
            <ul className="mt-2 space-y-1.5">
              {open.map((row) => (
                <li key={row.id} className="flex min-h-11 items-center gap-3 rounded-xl px-2.5 py-2">
                  <CheckBadge done={false} />
                  <span className="text-[15px] text-slate-800">{STEP_LIST_TITLES[row.id]}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[15px] text-slate-500">{DONE_COPY.remindNext}</p>
          </>
        )}
        <div className="mt-5">
          <button type="button" onClick={() => close('completed')} className={primaryClass}>
            {DONE_COPY.allDone.primary}
          </button>
        </div>
      </>
    )
  } else if (currentId === 'recording-consent') {
    content = (
      <RecordingConsentContent
        key={currentId}
        variant={consentVariant}
        resend={consentRequestPending}
        status={
          consentStatus && !consentRequestPending ? (
            <p className="text-[15px] font-medium text-slate-600">
              {consentVariant === 'acknowledge'
                ? 'You have read this ✓'
                : consentStatus === 'ACCEPTED'
                  ? 'Your answer now: Yes'
                  : 'Your answer now: No'}
            </p>
          ) : undefined
        }
        busy={consentBusy}
        error={error}
        autoFocusHeading
        headingId={headingId}
        result={consentResult}
        onAccept={() => void decideConsent(true)}
        onDecline={consentVariant === 'consent' ? () => decideConsent(false) : undefined}
        onLater={() => putOff('recording-consent')}
      />
    )
  } else if (currentId && currentRow) {
    content = (
      <SetupStepCard
        key={currentId}
        id={currentId}
        copy={stepCopy(currentId, copy)}
        row={currentRow}
        platform={copy.platform}
        canOpenSettings={canOpenAppSettings()}
        busy={busy}
        error={error}
        notice={notice && notice.id === currentId ? notice.text : null}
        headingRef={headingRef}
        headingId={headingId}
        onPrimary={() => void runPrimary()}
        onCheckAgain={() => void checkAgain()}
        onLater={() => putOff(currentId)}
        onNext={advance}
      />
    )
  } else {
    content = null
  }

  return (
    <div
      {...{ [ONBOARDING_TOUR_ATTR]: '', [SETUP_FLOW_ATTR]: '' }}
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm native-modal-top animate-tour-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
    >
      <div
        ref={dialogRef}
        className="animate-fade-slide-in flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-15px_rgba(76,29,149,0.35),0_8px_24px_-12px_rgba(15,23,42,0.25)]"
      >
        <div className="flex items-center justify-between gap-3 px-6 pt-5">
          <span className="text-sm font-semibold text-brand-700">Set up</span>
          {progress && (
            <span className="text-sm font-medium text-slate-500" aria-live="polite">
              {progress}
            </span>
          )}
        </div>
        <div className="overflow-y-auto px-6 pb-6 pt-3">{content}</div>
        <p className="sr-only" role="status" aria-live="polite">
          {announce}
        </p>
      </div>
    </div>
  )
}
